/**
 * Production-ready input validation service with comprehensive security checks.
 * 
 * This service provides validation for all user inputs to prevent:
 * - XSS attacks through input sanitization
 * - Invalid data from entering the system
 * - Security vulnerabilities through proper validation
 * - Data corruption through type checking
 * 
 * @example
 * ```typescript
 * import { ValidationService } from './validation';
 * 
 * const validator = ValidationService.getInstance();
 * 
 * if (!validator.validateDocumentId(documentId)) {
 *   throw new ValidationError('Invalid document ID format');
 * }
 * ```
 */

import { logger, LogContext } from './logger';

/**
 * Custom validation error class for better error handling
 */
export class ValidationError extends Error {
  public readonly field: string;
  public readonly code: string;

  constructor(message: string, field: string, code: string = 'VALIDATION_ERROR') {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
    this.code = code;
  }
}

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  sanitizedValue?: any;
}

/**
 * File validation options
 */
export interface FileValidationOptions {
  maxSize?: number;
  allowedTypes: string[];
  allowedExtensions: string[];
}

/**
 * Production-ready validation service with security-first approach
 */
export class ValidationService {
  private static instance: ValidationService;

  // Security patterns
  private readonly UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  private readonly EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  private readonly SAFE_STRING_PATTERN = /^[a-zA-Z0-9\s\-_.()]+$/;
  private readonly XSS_PATTERNS = [
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi
  ];

  private constructor() {}

  /**
   * Get singleton validation service instance
   */
  public static getInstance(): ValidationService {
    if (!ValidationService.instance) {
      ValidationService.instance = new ValidationService();
    }
    return ValidationService.instance;
  }

  /**
   * Validate document ID format (UUID)
   * 
   * @param documentId - Document ID to validate
   * @param context - Logging context for audit trail
   * @returns True if valid UUID format
   */
  public validateDocumentId(documentId: string, context?: Partial<LogContext>): boolean {
    const correlationId = context?.correlationId || logger.getCorrelationId();
    
    try {
      if (!documentId || typeof documentId !== 'string') {
        logger.warn('Document ID validation failed: empty or invalid type', {
          correlationId,
          operation: 'validateDocumentId',
          error: 'Empty or invalid type',
          metadata: { documentId: typeof documentId }
        });
        return false;
      }

      const isValid = this.UUID_PATTERN.test(documentId.trim());
      
      if (!isValid) {
        logger.warn('Document ID validation failed: invalid UUID format', {
          correlationId,
          operation: 'validateDocumentId',
          error: 'Invalid UUID format',
          metadata: { documentId: documentId.substring(0, 8) + '...' } // Log partial for security
        });
      }

      return isValid;
    } catch (error) {
      logger.error('Document ID validation error', {
        correlationId,
        operation: 'validateDocumentId',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      return false;
    }
  }

  /**
   * Validate page number for document viewing
   * 
   * @param pageNum - Page number to validate
   * @param maxPages - Maximum allowed pages
   * @param context - Logging context
   * @returns True if valid page number
   */
  public validatePageNumber(
    pageNum: number, 
    maxPages: number, 
    context?: Partial<LogContext>
  ): boolean {
    const correlationId = context?.correlationId || logger.getCorrelationId();
    
    try {
      if (!Number.isInteger(pageNum) || pageNum < 1 || pageNum > maxPages) {
        logger.warn('Page number validation failed', {
          correlationId,
          operation: 'validatePageNumber',
          error: 'Invalid page number',
          metadata: { pageNum, maxPages, isInteger: Number.isInteger(pageNum) }
        });
        return false;
      }

      return true;
    } catch (error) {
      logger.error('Page number validation error', {
        correlationId,
        operation: 'validatePageNumber',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      return false;
    }
  }

  /**
   * Validate and sanitize user input strings
   * 
   * @param input - Input string to validate
   * @param maxLength - Maximum allowed length
   * @param allowSpecialChars - Whether to allow special characters
   * @param context - Logging context
   * @returns Validation result with sanitized value
   */
  public validateAndSanitizeString(
    input: string,
    maxLength: number = 1000,
    allowSpecialChars: boolean = false,
    context?: Partial<LogContext>
  ): ValidationResult {
    const correlationId = context?.correlationId || logger.getCorrelationId();
    const errors: ValidationError[] = [];

    try {
      if (!input || typeof input !== 'string') {
        errors.push(new ValidationError('Input must be a non-empty string', 'input', 'INVALID_TYPE'));
        return { isValid: false, errors };
      }

      // Check length
      if (input.length > maxLength) {
        errors.push(new ValidationError(
          `Input exceeds maximum length of ${maxLength} characters`,
          'input',
          'MAX_LENGTH_EXCEEDED'
        ));
      }

      // Check for XSS patterns
      const hasXSS = this.XSS_PATTERNS.some(pattern => pattern.test(input));
      if (hasXSS) {
        logger.warn('XSS attempt detected in input', {
          correlationId,
          operation: 'validateAndSanitizeString',
          error: 'XSS pattern detected',
          metadata: { inputLength: input.length }
        });
        errors.push(new ValidationError('Input contains potentially malicious content', 'input', 'XSS_DETECTED'));
      }

      // Check character restrictions
      if (!allowSpecialChars && !this.SAFE_STRING_PATTERN.test(input)) {
        errors.push(new ValidationError(
          'Input contains invalid characters',
          'input',
          'INVALID_CHARACTERS'
        ));
      }

      if (errors.length > 0) {
        return { isValid: false, errors };
      }

      // Sanitize the input
      const sanitizedValue = this.sanitizeString(input);

      return {
        isValid: true,
        errors: [],
        sanitizedValue
      };
    } catch (error) {
      logger.error('String validation error', {
        correlationId,
        operation: 'validateAndSanitizeString',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      
      errors.push(new ValidationError('Validation error occurred', 'input', 'VALIDATION_ERROR'));
      return { isValid: false, errors };
    }
  }

  /**
   * Validate file upload
   * 
   * @param file - File to validate
   * @param options - Validation options
   * @param context - Logging context
   * @returns Validation result
   */
  public validateFile(
    file: File,
    options: FileValidationOptions,
    context?: Partial<LogContext>
  ): ValidationResult {
    const correlationId = context?.correlationId || logger.getCorrelationId();
    const errors: ValidationError[] = [];

    try {
      if (!file) {
        errors.push(new ValidationError('No file provided', 'file', 'FILE_REQUIRED'));
        return { isValid: false, errors };
      }

      // Validate file size (skipped when maxSize is not specified — caller handles it)
      if (options.maxSize !== undefined && file.size > options.maxSize) {
        errors.push(new ValidationError(
          `File size ${file.size} exceeds maximum allowed size of ${options.maxSize} bytes`,
          'file',
          'FILE_TOO_LARGE'
        ));
      }

      // Validate file type
      if (!options.allowedTypes.includes(file.type)) {
        errors.push(new ValidationError(
          `File type ${file.type} is not allowed`,
          'file',
          'INVALID_FILE_TYPE'
        ));
      }

      // Validate file extension
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !options.allowedExtensions.includes(fileExtension)) {
        errors.push(new ValidationError(
          `File extension .${fileExtension} is not allowed`,
          'file',
          'INVALID_FILE_EXTENSION'
        ));
      }

      // Validate filename
      const filenameValidation = this.validateAndSanitizeString(file.name, 255, true, { correlationId });
      if (!filenameValidation.isValid) {
        errors.push(...filenameValidation.errors);
      }

      if (errors.length > 0) {
        logger.warn('File validation failed', {
          correlationId,
          operation: 'validateFile',
          error: 'File validation failed',
          metadata: {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            errors: errors.map(e => e.message)
          }
        });
        return { isValid: false, errors };
      }

      logger.info('File validation successful', {
        correlationId,
        operation: 'validateFile',
        metadata: {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type
        }
      });

      return { isValid: true, errors: [] };
    } catch (error) {
      logger.error('File validation error', {
        correlationId,
        operation: 'validateFile',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      
      errors.push(new ValidationError('File validation error occurred', 'file', 'VALIDATION_ERROR'));
      return { isValid: false, errors };
    }
  }

  /**
   * Validate email address format
   * 
   * @param email - Email to validate
   * @param context - Logging context
   * @returns True if valid email format
   */
  public validateEmail(email: string, context?: Partial<LogContext>): boolean {
    const correlationId = context?.correlationId || logger.getCorrelationId();
    
    try {
      if (!email || typeof email !== 'string') {
        return false;
      }

      const isValid = this.EMAIL_PATTERN.test(email.trim().toLowerCase());
      
      if (!isValid) {
        logger.warn('Email validation failed', {
          correlationId,
          operation: 'validateEmail',
          error: 'Invalid email format'
        });
      }

      return isValid;
    } catch (error) {
      logger.error('Email validation error', {
        correlationId,
        operation: 'validateEmail',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      return false;
    }
  }

  /**
   * Sanitize string input by removing/escaping dangerous characters
   * 
   * @param input - String to sanitize
   * @returns Sanitized string
   */
  private sanitizeString(input: string): string {
    return input
      .trim()
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }

  /**
   * Rate limiting validation for API calls
   * 
   * @param key - Rate limit key (e.g., user ID, IP)
   * @param maxRequests - Maximum requests allowed
   * @param windowMs - Time window in milliseconds
   * @returns True if within rate limit
   */
  public checkRateLimit(key: string, maxRequests: number, windowMs: number): boolean {
    const now = Date.now();
    const rateLimitKey = `rateLimit_${key}`;
    
    try {
      const stored = sessionStorage.getItem(rateLimitKey);
      const requests = stored ? JSON.parse(stored) : [];
      
      // Remove old requests outside the window
      const validRequests = requests.filter((timestamp: number) => now - timestamp < windowMs);
      
      if (validRequests.length >= maxRequests) {
        logger.warn('Rate limit exceeded', {
          correlationId: logger.getCorrelationId(),
          operation: 'checkRateLimit',
          error: 'Rate limit exceeded',
          metadata: {
            key,
            requestCount: validRequests.length,
            maxRequests,
            windowMs
          }
        });
        return false;
      }
      
      // Add current request
      validRequests.push(now);
      sessionStorage.setItem(rateLimitKey, JSON.stringify(validRequests));
      
      return true;
    } catch (error) {
      logger.error('Rate limit check error', {
        correlationId: logger.getCorrelationId(),
        operation: 'checkRateLimit',
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      // Allow request on error to avoid blocking legitimate users
      return true;
    }
  }
}

// Export singleton instance for convenience
export const validator = ValidationService.getInstance();
export default validator;